import React, { Suspense } from "react";
import { useLocation, Route, Routes, Navigate, matchPath } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Container } from "reactstrap";

import AdminNavbar from "components/Navbars/AdminNavbar.js";
import AdminFooter from "components/Footers/AdminFooter.js";
import Sidebar, { groupForPath } from "components/Sidebar/Sidebar.js";
import ProtectedRoute from "components/ProtectedRoute.js";
import SessionTimeoutGuard from "components/SessionTimeoutGuard.js";
import ReauthProvider from "components/ReauthProvider.js";
import SessionRegistry from "components/SessionRegistry.js";
import { permissionForPath } from "services/roles";
import { AiAnalysisLauncher } from "components/AiAnalysis.js";
import { moduleForPath } from "services/aiAnalysis";

import routes from "routes.js";
import LoadingSpinner from "components/LoadingSpinner.js";

const Admin = (props) => {
  const { t } = useTranslation();
  const mainContent = React.useRef(null);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    document.body.classList.add("fd-admin");
    return () => document.body.classList.remove("fd-admin");
  }, []);

  React.useEffect(() => {
    setSidebarOpen(false);
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) mainContent.current.scrollTop = 0;
  }, [location]);

  const getRoutes = (routesList) => {
    return routesList.map((prop, key) => {
      if (prop.layout === "/admin") {
        const requiredPermission =
          prop.permission || permissionForPath(`${prop.layout}${prop.path}`);
        return (
          <Route
            path={prop.path}
            element={
              <ProtectedRoute permission={requiredPermission}>
                {prop.component}
              </ProtectedRoute>
            }
            key={key}
          />
        );
      }
      return null;
    });
  };

  const currentRoute = React.useMemo(() => {
    const adminRoutes = routes.filter((r) => r.layout === "/admin");
    const exact = adminRoutes
      .filter((r) => matchPath({ path: r.layout + r.path, end: true }, location.pathname))
      .sort((a, b) => (a.path.split(":").length - b.path.split(":").length));
    if (exact.length) return exact[0];
    const prefixed = adminRoutes
      .filter((r) => !r.path.includes(":") && location.pathname.startsWith(r.layout + r.path))
      .sort((a, b) => b.path.length - a.path.length);
    return prefixed[0] || null;
  }, [location.pathname]);

  const getBrandText = () => {
    if (!currentRoute) return "FutureDocument";
    return currentRoute.nameKey ? t(currentRoute.nameKey) : currentRoute.name;
  };

  const getGroupLabel = () => {
    const sub = location.pathname.replace(/^\/admin/, "");
    const id = groupForPath(sub);
    return id === "general" ? null : t(`navGroups.${id}`);
  };

  return (
    <>
      <a className="fd-skip-link" href="#main-content">{t("a11y.skipToContent")}</a>
      <Sidebar
        routes={routes}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        logo={{
          innerLink: "/admin/dashboard",
          imgSrc: require("../assets/img/brand/blue.png"),
          imgAlt: "FutureDocument"
        }}
      />

      <div className="main-content" ref={mainContent}>
        <AdminNavbar
          brandText={getBrandText()}
          groupLabel={getGroupLabel()}
          onMenuClick={() => setSidebarOpen(true)}
        />

        <main id="main-content" tabIndex={-1} style={{ outline: "none" }}>
          <ProtectedRoute>
            <SessionTimeoutGuard />
            <SessionRegistry />
            <ReauthProvider>
              <Suspense fallback={<LoadingSpinner size="lg" />}>
                <Routes>
                  {getRoutes(routes)}
                  <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Routes>
              </Suspense>
              {!location.pathname.startsWith("/admin/ai/analysis") && (
                <AiAnalysisLauncher moduleId={moduleForPath(location.pathname)} />
              )}
            </ReauthProvider>
          </ProtectedRoute>
        </main>
        <Container fluid>
          <AdminFooter />
        </Container>
      </div>
    </>
  );
};

export default Admin;
